import React, { useRef, useState } from 'react'
import Select from 'react-select'
import * as Utils from '../../commons/utils/Utils'
import {
	MAX_LOTE,
	MODO_ELEGIR,
	MODO_TODOS,
	construirCuerpoLote,
	cantidadAIniciar,
	quedanSinIniciar,
	puedeIniciar,
	resumirResultado,
} from './iniciarLoteHelpers'

/**
 * Modal "Iniciar validación de secuencia" (VEC-3474): inicia la secuencia en uno, varios o todos los vehículos disponibles.
 *
 * Disponible es el vehículo en el estado de origen de la secuencia, sin un intento en curso y con intentos libres, dentro de lo que el usuario puede
 * ver. El selector ofrece solo esos (la API los busca) y "Todos los disponibles" los toma hasta el tope por pedido. Cada vehículo se inicia por
 * separado: los que no se pueden se muestran al final con su motivo.
 *
 * @param intl        el `intl` de la grilla
 * @param secuencias las secuencias activas (con `movil_estado_origen` y `cantidad_maxima_intentos`)
 * @param alIniciar  se llama apenas la API responde, para que la grilla se actualice detrás del modal
 * @param success    cierra el modal
 * @param dismiss    cancela el modal
 */
const FormularioSecuenciaIntentoIniciarModal = (props) => {
	// Se muestra dentro de SweetAlert, fuera del contexto de react-intl: el `intl` llega por props, como en el resto de la grilla.
	const intl = props.intl;
	const [secuencia, setSecuencia] = useState(null);
	const [modo, setModo] = useState(MODO_ELEGIR);
	const [seleccionados, setSeleccionados] = useState([]);
	const [disponibles, setDisponibles] = useState(null);
	const [loading, setLoading] = useState(false);
	const [resultado, setResultado] = useState(null);
	// Una respuesta de otra secuencia (se cambió de opción mientras cargaba) no debe pisar el contador.
	const secuenciaVigente = useRef(null);

	const cambiarSecuencia = (nueva) => {
		secuenciaVigente.current = nueva ? nueva.id : null;
		setSecuencia(nueva);
		setSeleccionados([]);
		setModo(MODO_ELEGIR);
		setDisponibles(null);
		if (!nueva) return;

		Utils.getData('/tipo-formulario-secuencia/' + nueva.id + '/disponibles?limit=1')
			.then((res) => {
				if (secuenciaVigente.current === nueva.id) setDisponibles(res && res.count ? res.count : 0);
			})
			.catch(() => {
				if (secuenciaVigente.current === nueva.id) setDisponibles(0);
			});
	};

	const buscarVehiculos = (texto) => {
		return Utils.getData('/tipo-formulario-secuencia/' + secuencia.id + '/disponibles?limit=20&search=' + encodeURIComponent(texto || '')).then((res) => {
			return { options: res && res.items ? res.items : [] };
		});
	};

	const iniciar = (event) => {
		event.preventDefault();
		if (!puedeIniciar(secuencia, modo, seleccionados, disponibles, loading)) return;

		setLoading(true);
		Utils.sendData('/tipo-formulario-secuencia/' + secuencia.id + '/iniciar-lote', 'POST', construirCuerpoLote(modo, seleccionados))
			.then((res) => {
				if (props.alIniciar) props.alIniciar();
				const resumen = resumirResultado(res);
				// Un solo vehículo sin novedades: no hace falta mostrar el resultado.
				if (resumen.iniciados === 1 && resumen.omitidos === 0 && resumen.restantes === 0) {
					props.success();
					return;
				}
				setResultado(res);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const textoDelMotivo = (omitido) => {
		switch (omitido.motivo) {
			case 'estado_cambio':
				return intl.formatMessage(
					{ id: 'iniciarLote.motivo.estado_cambio', defaultMessage: 'Ahora está en estado {estado} (la secuencia aplica a {origen})' },
					{ estado: omitido.estado_actual || '-', origen: secuencia.movil_estado_origen }
				);
			case 'intento_en_curso':
				return intl.formatMessage({ id: 'iniciarLote.motivo.intento_en_curso', defaultMessage: 'Ya tiene un intento en curso' });
			case 'en_otra_secuencia':
				return omitido.secuencia_en_curso
					? intl.formatMessage({ id: 'iniciarLote.motivo.en_otra_secuencia', defaultMessage: 'Ya está en la secuencia {secuencia}' }, { secuencia: omitido.secuencia_en_curso })
					: intl.formatMessage({ id: 'iniciarLote.motivo.en_otra_secuencia_sin_nombre', defaultMessage: 'Ya está en otra secuencia' });
			case 'intentos_agotados':
				return intl.formatMessage({ id: 'iniciarLote.motivo.intentos_agotados', defaultMessage: 'Agotó los intentos de la secuencia' });
			case 'sin_acceso':
				return intl.formatMessage({ id: 'iniciarLote.motivo.sin_acceso', defaultMessage: 'No tenés acceso a este vehículo' });
			default:
				return intl.formatMessage({ id: 'iniciarLote.motivo.otro', defaultMessage: 'No se pudo iniciar' });
		}
	};

	const aviso = { borderRadius: 4, padding: '10px 12px', fontSize: '0.9rem', marginBottom: 14, textAlign: 'left' };

	if (resultado) {
		const resumen = resumirResultado(resultado);
		return (
			<div className="text-left">
				<div className="d-flex mb-2" style={{ gap: 10 }}>
					<div style={{ flex: 1, background: '#e7f5ea', color: '#1f8a3b', borderRadius: 4, padding: '8px 12px' }}>
						<strong style={{ display: 'block', fontSize: '1.4rem' }}>{resumen.iniciados}</strong>
						{intl.formatMessage({ id: 'iniciarLote.resultado.iniciados', defaultMessage: 'iniciados' })}
					</div>
					<div style={{ flex: 1, background: '#fff3d9', color: '#a45f00', borderRadius: 4, padding: '8px 12px' }}>
						<strong style={{ display: 'block', fontSize: '1.4rem' }}>{resumen.omitidos}</strong>
						{intl.formatMessage({ id: 'iniciarLote.resultado.omitidos', defaultMessage: 'omitidos' })}
					</div>
				</div>
				{resumen.omitidos > 0 && (
					<ul className="list-unstyled mb-2" style={{ border: '1px solid #dfe3ee', borderRadius: 4, maxHeight: 190, overflow: 'auto' }}>
						{resultado.omitidos.map((o) => (
							<li key={o.movil_id} className="d-flex justify-content-between" style={{ gap: 10, padding: '7px 10px', borderBottom: '1px solid #dfe3ee' }}>
								<strong style={{ whiteSpace: "nowrap" }}>{o.dominio || "#" + o.movil_id}</strong>
								<span className="text-muted text-right">{textoDelMotivo(o)}</span>
							</li>
						))}
					</ul>
				)}
				{resumen.restantes > 0 && (
					<div style={Object.assign({}, aviso, { background: '#fff3d9', color: '#a45f00' })}>
						{intl.formatMessage(
							{ id: 'iniciarLote.resultado.restantes', defaultMessage: 'Quedan {cantidad} vehículos disponibles sin iniciar: repetí la operación.' },
							{ cantidad: resumen.restantes }
						)}
					</div>
				)}
				<div className="d-flex justify-content-end mt-2">
					<button type="button" className="btn btn-primary" onClick={() => props.success()}>
						{intl.formatMessage({ id: 'iniciarLote.boton.cerrar', defaultMessage: 'Cerrar' })}
					</button>
				</div>
			</div>
		);
	}

	const cantidad = cantidadAIniciar(modo, seleccionados, disponibles);
	const sobrantes = quedanSinIniciar(modo, disponibles);

	return (
		<form className="form form-horizontal" onSubmit={iniciar}>
			<div className="form-group text-left">
				<label>{intl.formatMessage({ id: 'iniciarLote.campo.secuencia', defaultMessage: 'Secuencia *' })}:</label>
				<Select
					options={props.secuencias}
					valueKey="id"
					labelKey="nombre"
					value={secuencia}
					onChange={cambiarSecuencia}
					disabled={loading}
					multi={false}
				/>
				{secuencia && (
					<small className="text-muted d-block mt-1">
						{intl.formatMessage(
							{ id: 'iniciarLote.secuencia.aplica_a', defaultMessage: 'Aplica a vehículos en estado {estado} · hasta {intentos} intentos' },
							{ estado: secuencia.movil_estado_origen, intentos: secuencia.cantidad_maxima_intentos }
						)}
					</small>
				)}
			</div>

			{secuencia && disponibles === 0 && (
				<div style={Object.assign({}, aviso, { background: '#fff3d9', color: '#a45f00' })}>
					{intl.formatMessage({ id: 'iniciarLote.sin_disponibles', defaultMessage: 'No hay vehículos disponibles para iniciar esta secuencia.' })}
				</div>
			)}

			{secuencia && disponibles > 0 && (
				<div className="form-group text-left">
					<label>{intl.formatMessage({ id: 'iniciarLote.campo.vehiculos', defaultMessage: 'Vehículos *' })}:</label>
					<div className="btn-group btn-group-sm mb-2 d-flex" role="group">
						<button
							type="button"
							className={'btn ' + (modo === MODO_ELEGIR ? 'btn-primary' : 'btn-outline-secondary')}
							onClick={() => setModo(MODO_ELEGIR)}
							disabled={loading}
						>
							{intl.formatMessage({ id: 'iniciarLote.modo.elegir', defaultMessage: 'Elegir vehículos' })}
						</button>
						<button
							type="button"
							className={'btn ' + (modo === MODO_TODOS ? 'btn-primary' : 'btn-outline-secondary')}
							onClick={() => setModo(MODO_TODOS)}
							disabled={loading}
						>
							{intl.formatMessage({ id: 'iniciarLote.modo.todos', defaultMessage: 'Todos los disponibles ({cantidad})' }, { cantidad: disponibles })}
						</button>
					</div>
					{modo === MODO_ELEGIR && (
						<Select.Async
							key={secuencia.id}
							multi={true}
							value={seleccionados}
							valueKey="id"
							labelKey="dominio"
							loadOptions={buscarVehiculos}
							cache={false}
							onChange={(valores) => setSeleccionados(valores || [])}
							disabled={loading}
							placeholder={intl.formatMessage({ id: 'iniciarLote.vehiculos.placeholder', defaultMessage: 'Buscar por dominio…' })}
							searchPromptText={intl.formatMessage({ id: 'iniciarLote.vehiculos.escribir', defaultMessage: 'Escribí para buscar' })}
							noResultsText={intl.formatMessage({ id: 'iniciarLote.vehiculos.sin_resultados', defaultMessage: 'Sin resultados' })}
						/>
					)}
				</div>
			)}

			{secuencia && disponibles > 0 && cantidad > 0 && (
				<div style={Object.assign({}, aviso, modo === MODO_TODOS ? { background: '#fff3d9', color: '#a45f00' } : { background: '#e6f8f7', color: '#007a78' })}>
					{modo === MODO_TODOS
						? intl.formatMessage(
								{
									id: 'iniciarLote.resumen.todos',
									defaultMessage: 'Se van a iniciar {cantidad, plural, one {# vehículo} other {# vehículos}} en estado {estado}.',
								},
								{ cantidad: cantidad, estado: secuencia.movil_estado_origen }
						  )
						: intl.formatMessage(
								{ id: 'iniciarLote.resumen.elegidos', defaultMessage: '{cantidad, plural, one {# vehículo seleccionado} other {# vehículos seleccionados}}' },
								{ cantidad: cantidad }
						  )}
					{sobrantes > 0 && (
						<div className="mt-1">
							{intl.formatMessage(
								{
									id: 'iniciarLote.resumen.tope',
									defaultMessage: 'Se procesan hasta {tope} por vez. Quedan {cantidad} más: repetí la operación.',
								},
								{ tope: MAX_LOTE, cantidad: sobrantes }
							)}
						</div>
					)}
				</div>
			)}

			<div className="d-flex justify-content-end mt-2">
				<button type="submit" className="btn btn-primary mr-1" disabled={!puedeIniciar(secuencia, modo, seleccionados, disponibles, loading)}>
					<i className="fa fa-check-circle mr-1"></i>
					{intl.formatMessage({ id: 'iniciarLote.boton.iniciar', defaultMessage: 'Iniciar' })}
				</button>
				<button type="button" className="btn btn-danger" onClick={() => props.dismiss()}>
					<i className="fa fa-times-circle mr-1"></i> {intl.formatMessage({ id: 'iniciarLote.boton.cancelar', defaultMessage: 'Cancelar' })}
				</button>
			</div>
		</form>
	);
};

export default FormularioSecuenciaIntentoIniciarModal;
